import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../context/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';

const RATE_LIMIT_COOLDOWN_SECONDS = 60;
const QUEUE_MESSAGE_PREFIX = 'Сейчас наблюдается повышенная активность пользователей, просим немного подождать ответа. Ваш номер в очереди:';

// Новый компонент для индикатора печати
const TypingIndicator = ({ queuePosition }) => (
  <div className="typing-indicator-ui">
    <span className="icon">{Number(queuePosition) > 1 ? '⏳' : '✍️'}</span>
    {Number(queuePosition) > 1 ? (
      <span className="text">{QUEUE_MESSAGE_PREFIX} {queuePosition}</span>
    ) : (
      <>
        <span className="text">Печатает</span>
        <span className="dot">.</span>
        <span className="dot">.</span>
        <span className="dot">.</span>
      </>
    )}
  </div>
);

// Модальное окно анализа с поддержкой повторной попытки
const AnalysisModal = ({ analysis, error, errorMessage, onRetry, retryInSeconds, canRetry, onClose, queuePosition }) => {
  const isDark = document.documentElement.classList.contains('dark');
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border flex flex-col`}>
        {/* Header */}
        <div className={`${isDark ? 'bg-gradient-to-r from-blue-900 to-indigo-900' : 'bg-gradient-to-r from-blue-600 to-indigo-600'} px-6 py-4 flex justify-between items-center`}>
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <span className="text-2xl">📊</span>
            Анализ диалога
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-3xl font-light transition-colors">&times;</button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {error ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-700 dark:text-red-400">
                <span className="text-2xl">⚠️</span>
                {errorMessage || 'Ошибка при получении анализа. Напишите позже.'}
              </div>
              <button
                onClick={onRetry}
                disabled={!canRetry}
                className={`w-full font-semibold py-3 px-6 rounded-xl transition-all ${canRetry
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-not-allowed'
                  }`}
              >
                {canRetry ? 'Повторить попытку' : `Повторить через ${retryInSeconds} сек`}
              </button>
            </div>
          ) : analysis ? (
            <MarkdownRenderer variant="analysis" darkMode={isDark}>
              {analysis}
            </MarkdownRenderer>
          ) : (
            <div className="flex items-center justify-center gap-3 py-12 text-gray-500">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              {Number(queuePosition) > 1 ? `${QUEUE_MESSAGE_PREFIX} ${queuePosition}` : 'Загрузка анализа...'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t`}>
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

// Новый компонент для отображения достижений
const AchievementsModal = ({ achievements, onClose }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
    background: 'rgba(0,0,0,0.35)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{
      background: 'white', borderRadius: 14, maxWidth: '95vw', width: '94%', padding: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#22577A', margin: 0 }}>Поздравляем! Новые достижения:</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: 22, fontWeight: 700, cursor: 'pointer', padding: 4, marginLeft: 8 }}>&times;</button>
      </div>
      <ul style={{ marginBottom: 10, color: '#22577A', fontSize: 13, fontWeight: 500 }}>
        {achievements.map((ach, idx) => (
          <li key={idx} style={{ marginBottom: 4 }}>🏆 {ach}</li>
        ))}
      </ul>
      <button
        onClick={onClose}
        style={{ background: '#22577A', color: 'white', border: 'none', borderRadius: 7, padding: '12px 0', fontWeight: 600, fontSize: 15, cursor: 'pointer', width: '100%', marginTop: 4 }}>
        Закрыть
      </button>
    </div>
  </div>
);

const Chat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUser, refreshLimits, limits } = useAuth();
  const scenario = location.state?.scenario || null;
  const isTimed = location.state?.isTimed || false;
  const variableValues = location.state?.variableValues || {};
  const sessionStarted = location.state?.sessionStarted || false;

  // Хелпер форматирования времени без миллисекунд
  const formatTime = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    } catch {
      return isoString;
    }
  };

  const [dialogId, setDialogId] = useState(null);
  const [messages, setMessages] = useState([]); // { role: 'user' | 'assistant', text: string, typing?: boolean }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const chatContainerRef = useRef(null);
  const [analysisError, setAnalysisError] = useState(false);
  const [analysisErrorMessage, setAnalysisErrorMessage] = useState('');
  const [newAchievements, setNewAchievements] = useState([]);
  const [analysisClosed, setAnalysisClosed] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState('');
  const [sendBlockedUntil, setSendBlockedUntil] = useState(0);
  const [analysisRetryBlockedUntil, setAnalysisRetryBlockedUntil] = useState(0);
  const [timeTick, setTimeTick] = useState(Date.now());
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [duration, setDuration] = useState(null);
  const [timer, setTimer] = useState(0);
  const [queuePosition, setQueuePosition] = useState(null);
  // Новые состояния для истории и статуса диалога
  const [sessions, setSessions] = useState([]);
  const [dialogStatus, setDialogStatus] = useState('active'); // 'active' | 'completed'
  const [selectedScenarioName, setSelectedScenarioName] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'active' | 'completed'
  const [showArchived, setShowArchived] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [showHistoryMobile, setShowHistoryMobile] = useState(false);
  // Новое: сворачивание истории на десктопе
  const [showSidebarDesktop, setShowSidebarDesktop] = useState(true);
  // Режим компактной (свернутой) истории на десктопе
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Модалка овердрафта
  const [showOverdraftModal, setShowOverdraftModal] = useState(false);
  // Потраченные токены в текущем диалоге
  const [dialogTokensUsed, setDialogTokensUsed] = useState(0);
  // Debug info для админа
  const [debugInfo, setDebugInfo] = useState(null);
  // Скрыть debug консоль (сохраняется в localStorage)
  const [debugHidden, setDebugHidden] = useState(() => localStorage.getItem('debugHidden') === 'true');

  const pollJob = async (jobId, type, specificDialogId) => {
    const token = ensureTokenOrRedirect();
    if (!token) return null;
    const currentDialogId = specificDialogId || dialogId;
    try {
      const res = await fetch(`/api/chat/session/${currentDialogId}/job/${jobId}?type=${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.status === 202) {
        const data = await res.json();
        const position = Number(data.position);
        if (Number.isFinite(position) && position > 1) {
          setQueuePosition(position);
        } else {
          setQueuePosition(null);
        }

        await new Promise(r => setTimeout(r, 1500));
        return pollJob(jobId, type, currentDialogId);
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Ошибка при получении ответа');
      }
      setQueuePosition(null);
      return await res.json();
    } catch (e) {
      setQueuePosition(null);
      throw e;
    }
  };

  // Хелпер: проверяем наличие JWT и мягко редиректим
  const ensureTokenOrRedirect = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Требуется вход. Пожалуйста, авторизуйтесь.');
      navigate('/login');
      return null;
    }
    return token;
  };

  const secondsLeft = (untilMs) => Math.max(0, Math.ceil((untilMs - timeTick) / 1000));
  const sendBlockSeconds = secondsLeft(sendBlockedUntil);
  const analysisRetrySeconds = secondsLeft(analysisRetryBlockedUntil);
  const isSendTemporarilyBlocked = sendBlockSeconds > 0;
  const isAnalysisRetryBlocked = analysisRetrySeconds > 0;

  const isRateLimitError = (data, rawMessage = '') => {
    const payloadText = `${rawMessage || ''} ${data?.error || ''} ${data?.details?.error || ''}`.toLowerCase();
    return (
      data?.error_code === 'RATE_LIMIT' ||
      data?.status === 429 ||
      payloadText.includes('429') ||
      payloadText.includes('rate limit') ||
      payloadText.includes('too many requests') ||
      payloadText.includes('слишком много запросов')
    );
  };

  const activateSendCooldown = (seconds = RATE_LIMIT_COOLDOWN_SECONDS, message = 'Слишком много запросов. Напишите позже.') => {
    const blockUntil = Date.now() + seconds * 1000;
    setSendBlockedUntil(prev => Math.max(prev, blockUntil));
    setError(`${message} (${seconds} сек)`);
  };

  const activateAnalysisRetryCooldown = (seconds = RATE_LIMIT_COOLDOWN_SECONDS) => {
    const blockUntil = Date.now() + seconds * 1000;
    setAnalysisRetryBlockedUntil(prev => Math.max(prev, blockUntil));
  };

  // Восстановление ID диалога при перезагрузке
  useEffect(() => {
    if (!location.state?.scenario) {
      const savedId = localStorage.getItem('currentDialogId');
      if (savedId) {
        setDialogId(savedId);
      }
    }
  }, []);

  // Сохранение ID диалога
  useEffect(() => {
    if (dialogId) {
      localStorage.setItem('currentDialogId', dialogId);
    }
  }, [dialogId]);

  // Восстановление имени сценария при загрузке списка сессий
  useEffect(() => {
    if (dialogId && sessions.length > 0 && !selectedScenarioName) {
      const session = sessions.find(s => s.id === dialogId);
      if (session) {
        setSelectedScenarioName(session.scenario_name);
      }
    }
  }, [dialogId, sessions, selectedScenarioName]);

  // Загрузка истории сессий
  const fetchSessions = async (status = filterStatus, archivedOnly = showArchived) => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    try {
      const qs = new URLSearchParams();
      if (status && status !== 'all') qs.set('status', status);
      if (archivedOnly) qs.set('archived_only', 'true');
      const res = await fetch(`/api/chat/sessions${qs.toString() ? `?${qs.toString()}` : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Не удалось загрузить список диалогов.');
      const data = await res.json();
      const rawSessions = Array.isArray(data.sessions) ? data.sessions : [];
      const sortedSessions = [...rawSessions].sort((a, b) => {
        const aActive = a.status === 'active';
        const bActive = b.status === 'active';
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        const aTime = new Date(a.completed_at || a.updated_at || a.started_at || 0).getTime();
        const bTime = new Date(b.completed_at || b.updated_at || b.started_at || 0).getTime();
        return bTime - aTime; // более новые выше
      });
      setSessions(sortedSessions);
    } catch (e) { }
  };

  const handleChangeFilter = (newFilter) => {
    setFilterStatus(newFilter);
    fetchSessions(newFilter, showArchived);
  };

  const toggleArchivedView = () => {
    const next = !showArchived;
    setShowArchived(next);
    // При показе архива игнорируем статус фильтра, показываем все архивные
    fetchSessions('all', next);
  };

  const handleArchive = async (dialogIdToArchive) => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    try {
      const res = await fetch(`/api/chat/session/${dialogIdToArchive}/archive`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Не удалось архивировать диалог');
      toast.success('Диалог скрыт');
      fetchSessions(filterStatus, showArchived);
      if (dialogId === dialogIdToArchive) {
        setDialogId(null);
        localStorage.removeItem('currentDialogId');
        setMessages([]);
        setDialogStatus('active');
        setSelectedScenarioName(null);
      }
    } catch (e) {
      toast.error('Ошибка при скрытии диалога');
    }
  };

  const handleRestore = async (dialogIdToRestore) => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    try {
      const res = await fetch(`/api/chat/session/${dialogIdToRestore}/restore`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Не удалось восстановить диалог');
      toast.success('Диалог восстановлен');
      fetchSessions(filterStatus, showArchived);
      if (dialogId === dialogIdToRestore && showArchived) {
        // Если в архивном режиме восстановили текущий — уберём из просмотра
        setDialogId(null);
        setMessages([]);
        setDialogStatus('active');
        setSelectedScenarioName(null);
      }
    } catch (e) {
      toast.error('Ошибка при восстановлении диалога');
    }
  };

  useEffect(() => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    fetchSessions();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setTimeTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!scenario) {
      // Нет сценария — просто режим истории
    } else {
      // Если сессия уже была начата (например, при перезагрузке страницы), 
      // не создаём новую, а восстанавливаем старую
      if (sessionStarted) {
        if (!dialogId) {
          const savedId = localStorage.getItem('currentDialogId');
          if (savedId) setDialogId(savedId);
        }
        return;
      }

      const startSession = async () => {
        const token = ensureTokenOrRedirect();
        if (!token) return;
        try {
          setLoading(true);
          const res = await fetch('/api/chat/session/start', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              scenario_id: scenario.id,
              variable_values: variableValues
            }),
          });
          if (!res.ok && res.status !== 202) throw new Error('Не удалось начать сессию.');
          const data = await res.json();
          setDialogId(data.dialog_id);
          setDialogStatus('active');
          setSelectedScenarioName(scenario?.name || null);
          setDialogTokensUsed(0); // Сброс счётчика токенов для нового диалога
          setLastFailedMessage('');
          setSendBlockedUntil(0);
          setAnalysisRetryBlockedUntil(0);
          setAnalysisErrorMessage('');

          if (res.status === 202 || data.status === 'queued') {
            setQueuePosition(data.position || 1);
            const jobData = await pollJob(data.job_id, 'start', data.dialog_id);
            if (jobData && jobData.first_ai_message && jobData.first_ai_message.text) {
              setMessages([{ role: jobData.first_ai_message.sender, text: jobData.first_ai_message.text }]);
            } else {
              setMessages([]);
            }
          } else {
            // Если сервер вернул первое сообщение ИИ — добавим сразу
            if (data.first_ai_message && data.first_ai_message.text) {
              setMessages([{ role: data.first_ai_message.sender, text: data.first_ai_message.text }]);
            } else {
              setMessages([]);
            }
          }
          // Обновим список сессий сразу, чтобы активный диалог был сверху
          fetchSessions('all', showArchived);

          // Помечаем в истории навигации, что сессия начата, чтобы при перезагрузке не создавать новую
          navigate('.', {
            state: { ...location.state, sessionStarted: true },
            replace: true
          });

        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      startSession();
    }
  }, [scenario, navigate, sessionStarted]);

  useEffect(() => {
    if (dialogId) {
      const fetchMessages = async () => {
        const token = ensureTokenOrRedirect();
        if (!token) return;
        try {
          setLoading(true);
          const res = await fetch(`/api/chat/session/${dialogId}/messages`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!res.ok) throw new Error('Не удалось загрузить историю сообщений.');
          const data = await res.json();
          const formattedMessages = data.messages.map(m => ({
            role: m.sender,
            text: m.text,
          }));
          setMessages(formattedMessages);
          if (data.status) setDialogStatus(data.status);
          // Загружаем потраченные токены в диалоге
          if (data.tokens_used !== undefined) {
            setDialogTokensUsed(data.tokens_used);
          }
          // Показать отчёт если диалог завершён
          if (data.analysis && data.status === 'completed') {
            setAnalysis(data.analysis);
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchMessages();
    }
  }, [dialogId]);

  useEffect(() => {
    if (analysisClosed) {
      setAnalysisClosed(false);
      setMessages([]);
      setDialogId(null);
      localStorage.removeItem('currentDialogId');
      setInput('');
      setError(null);
      setAnalysis(null);
      setAnalysisError(false);
      setAnalysisErrorMessage('');
      setNewAchievements([]);
      setLastFailedMessage('');
      setSendBlockedUntil(0);
      setAnalysisRetryBlockedUntil(0);
      // Обновляем историю
      fetchSessions();
      navigate('/achievements');
    }
  }, [analysisClosed, navigate]);

  useEffect(() => {
    if (newAchievements && newAchievements.length > 0) {
      newAchievements.forEach(ach => {
        toast.success(`Достижение выполнено: ${ach}`, {
          position: 'top-right',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });
      });
    }
  }, [newAchievements]);

  useEffect(() => {
    if (isTimed && dialogId && !startTime) {
      setStartTime(Date.now());
      setTimer(0);
    }
  }, [isTimed, dialogId, startTime]);

  useEffect(() => {
    let interval = null;
    if (isTimed && startTime && !endTime) {
      interval = setInterval(() => {
        setTimer(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => interval && clearInterval(interval);
  }, [isTimed, startTime, endTime]);

  // Автоскролл при новых сообщениях
  useEffect(() => {
    if (!userScrolledUp && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, userScrolledUp]);

  // Функция выбора сессии из истории
  const handleSelectSession = async (session) => {
    // Загружаем выбранный диалог в режим просмотра (без автоскролла)
    setDialogId(session.id);
    setSelectedScenarioName(session.scenario_name || null);
    setAnalysis(null);
    setShowAnalysisModal(false);
    setAnalysisError(false);
    setAnalysisErrorMessage('');
    setNewAchievements([]);
    setError(null);
    setLastFailedMessage('');
    setSendBlockedUntil(0);
    setAnalysisRetryBlockedUntil(0);
  };

  const sendMessage = async (customText) => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    if (loading) return;
    if (dialogStatus !== 'active') return;
    if (isSendTemporarilyBlocked) {
      setError(`Слишком много запросов. Напишите позже (${sendBlockSeconds} сек)`);
      return;
    }
    // Проверка баланса токенов (null = безлимит)
    if (limits?.total_available !== null && (limits?.total_available ?? 0) <= 0) {
      setShowOverdraftModal(true);
      return;
    }

    const textToSend = customText !== undefined ? customText : input;
    if (!textToSend.trim() || !dialogId) return;

    const rollbackOptimisticMessage = () => {
      setMessages(prev => {
        if (!prev.length) return prev;
        const last = prev[prev.length - 1];
        if (last.role === 'user' && last.text === textToSend) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    };

    setInput('');
    setError(null);
    setLastFailedMessage('');
    // Оптимистично добавляем сообщение пользователя (без автоскролла)
    setMessages(prev => [
      ...prev,
      { role: 'user', text: textToSend }
    ]);
    setLoading(true);

    try {
      const res = await fetch(`/api/chat/session/${dialogId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: textToSend }),
      });

      let data = {};
      try {
        if (res.status === 202) {
          const queuedData = await res.json();
          if (queuedData.status === 'queued') {
            setQueuePosition(queuedData.position || 1);
            data = await pollJob(queuedData.job_id, 'message');
          } else {
            data = queuedData;
          }
        } else {
          data = await res.json();
        }
      } catch (e) {
        rollbackOptimisticMessage();
        setInput(textToSend);
        setLastFailedMessage(textToSend);
        setError('Ошибка сервера: получен невалидный ответ. Можно повторить отправку.');
        setQueuePosition(null);
        return;
      }

      if (!res.ok && res.status !== 202 || data.error) {
        rollbackOptimisticMessage();
        setInput(textToSend);
        setLastFailedMessage(textToSend);
        const errMessage = data.error || 'Ошибка отправки сообщения. Можно повторить попытку.';
        setError(errMessage);
        setQueuePosition(null);
        if (isRateLimitError(data, errMessage)) {
          const retryAfter = Number(data.retry_after_seconds) > 0 ? Number(data.retry_after_seconds) : RATE_LIMIT_COOLDOWN_SECONDS;
          activateSendCooldown(retryAfter, 'Слишком много запросов. Напишите позже.');
        }
        return;
      }

      if (data.analysis) {
        setAnalysis(data.analysis);
        setShowAnalysisModal(true);
        setAnalysisError(false);
        setAnalysisErrorMessage('');
        setMessages(prev => [...prev, { role: 'system', text: data.analysis }]);
        if (data.achievements && data.achievements.length > 0) {
          setNewAchievements(data.achievements);
        }
        setDialogStatus('completed');
        fetchSessions();
      } else if (data.user_message && data.ai_message) {
        // Не добавляем user_message, так как уже добавили оптимистично
        setMessages(prev => [
          ...prev,
          { role: data.ai_message.sender, text: data.ai_message.text }
        ]);
        if (data.tokens_balance !== undefined) {
          updateUser({ tokens_balance: data.tokens_balance });
          refreshLimits();
        }
        if (data.dialog_tokens_used !== undefined) {
          setDialogTokensUsed(data.dialog_tokens_used);
        }
        if (data.debug) {
          setDebugInfo(data.debug);
        }
        if (data.warning && data.warning.warning === 'low_balance') {
          toast.warning(`⚠️ Низкий баланс токенов: осталось ${data.warning.tokens_remaining}`, { autoClose: 5000 });
        }
      } else if (data.ai_message) {
        setMessages(prev => [
          ...prev,
          { role: data.ai_message.sender || 'assistant', text: data.ai_message.text || data.response || '' }
        ]);
        if (data.tokens_balance !== undefined) {
          updateUser({ tokens_balance: data.tokens_balance });
          refreshLimits();
        }
        if (data.dialog_tokens_used !== undefined) {
          setDialogTokensUsed(data.dialog_tokens_used);
        }
      } else if (data.sender && data.text) {
        if (data.sender !== 'user') {
          setMessages(prev => [
            ...prev,
            { role: data.sender, text: data.text }
          ]);
        }
      }
    } catch (err) {
      rollbackOptimisticMessage();
      setInput(textToSend);
      setLastFailedMessage(textToSend);
      const errMessage = err.message || 'Ошибка отправки сообщения. Можно повторить попытку.';
      setError(errMessage);
      if (isRateLimitError(null, errMessage)) {
        activateSendCooldown(RATE_LIMIT_COOLDOWN_SECONDS, 'Слишком много запросов. Напишите позже.');
      }
    } finally {
      setLoading(false);
      setQueuePosition(null);
    }
  };
  // Завершение симуляции 
  const handleFinish = async () => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    if (loading) return; // Предотвращаем двойные вызовы
    if (dialogStatus !== 'active') return; // Уже завершён
    if (isSendTemporarilyBlocked) {
      setError(`Слишком много запросов. Напишите позже (${sendBlockSeconds} сек)`);
      return;
    }

    let calculatedDuration = 0;

    // Если диалог таймированный, рассчитываем длительность
    if (isTimed && startTime) {
      const finishTime = Date.now();
      setEndTime(finishTime);
      calculatedDuration = Math.floor((finishTime - startTime) / 1000);
      setDuration(calculatedDuration);
    }

    setLoading(true);
    setError(null);
    setAnalysisError(false);
    setAnalysisErrorMessage('');

    // Оптимистично добавляем сообщение пользователя
    setMessages(prev => [
      ...prev,
      { role: 'user', text: 'ЗАВЕРШИТЬ СИМУЛЯЦИЮ' }
    ]);

    try {
      // Отправляем сообщение о завершении с длительностью
      const res = await fetch(`/api/chat/session/${dialogId}/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: 'ЗАВЕРШИТЬ СИМУЛЯЦИЮ',
          duration: calculatedDuration // Отправляем duration вместе с сообщением
        }),
      });

      let data;
      try {
        if (res.status === 202) {
          const queuedData = await res.json();
          if (queuedData.status === 'queued') {
            setQueuePosition(queuedData.position || 1);
            setShowAnalysisModal(true);
            data = await pollJob(queuedData.job_id, 'analysis');
          } else {
            data = queuedData;
          }
        } else {
          data = await res.json();
        }
      } catch (e) {
        throw new Error('Ошибка сервера: получен невалидный ответ.');
      }

      if (!res.ok && res.status !== 202) {
        const errMessage = data.error || 'Ошибка при завершении диалога';
        if (isRateLimitError(data, errMessage)) {
          const retryAfter = Number(data.retry_after_seconds) > 0 ? Number(data.retry_after_seconds) : RATE_LIMIT_COOLDOWN_SECONDS;
          activateSendCooldown(retryAfter, 'Слишком много запросов. Напишите позже.');
          activateAnalysisRetryCooldown(retryAfter);
          setAnalysisErrorMessage('Слишком много запросов к анализу. Напишите позже.');
        } else {
          setAnalysisErrorMessage(errMessage);
        }
        setAnalysisError(true);
        setShowAnalysisModal(true);
        throw new Error(errMessage);
      }

      // Обрабатываем успешный ответ
      if (data.analysis) {
        setAnalysis(data.analysis);
        setShowAnalysisModal(true);
        setAnalysisError(false);
        setAnalysisErrorMessage('');
        setAnalysisRetryBlockedUntil(0);

        // Добавляем системное сообщение с анализом
        if (data.analysis_message) {
          setMessages(prev => [...prev, {
            role: 'system',
            text: data.analysis_message.text
          }]);
        }

        // Обрабатываем достижения
        if (data.achievements && data.achievements.length > 0) {
          setNewAchievements(data.achievements);
        }
        // Диалог завершён, блокируем ввод
        setDialogStatus('completed');
        fetchSessions();
      } else if (data.error || data.error_code) {
        const errMessage = data.error || 'Не удалось получить анализ. Напишите позже.';
        setAnalysisError(true);
        setAnalysisErrorMessage(errMessage);
        setShowAnalysisModal(true);
        setError(errMessage);
        setDialogStatus('completed');
        fetchSessions();
        if (isRateLimitError(data, errMessage)) {
          const retryAfter = Number(data.retry_after_seconds) > 0 ? Number(data.retry_after_seconds) : RATE_LIMIT_COOLDOWN_SECONDS;
          activateSendCooldown(retryAfter, 'Слишком много запросов. Напишите позже.');
          activateAnalysisRetryCooldown(retryAfter);
        }
      } else {
        const fallbackMessage = 'Не удалось получить анализ. Напишите позже.';
        setAnalysisError(true);
        setAnalysisErrorMessage(fallbackMessage);
        setShowAnalysisModal(true);
        setError(fallbackMessage);
        setDialogStatus('completed');
        fetchSessions();
      }

    } catch (err) {
      console.error('Ошибка при завершении диалога:', err);
      const errMessage = err.message || 'Ошибка при завершении диалога';
      setError(errMessage);
      if (!showAnalysisModal) {
        setAnalysisError(true);
        setAnalysisErrorMessage(errMessage);
        setShowAnalysisModal(true);
      }
      if (isRateLimitError(null, errMessage)) {
        activateSendCooldown(RATE_LIMIT_COOLDOWN_SECONDS, 'Слишком много запросов. Напишите позже.');
        activateAnalysisRetryCooldown(RATE_LIMIT_COOLDOWN_SECONDS);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetryAnalysis = async () => {
    const token = ensureTokenOrRedirect();
    if (!token) return;
    if (!dialogId || loading) return;
    if (isAnalysisRetryBlocked) return;

    // Блокируем повторное нажатие кнопки на 1 минуту
    activateAnalysisRetryCooldown(RATE_LIMIT_COOLDOWN_SECONDS);
    setLoading(true);
    setAnalysisError(false);
    setAnalysisErrorMessage('');
    setAnalysis(null);

    try {
      const res = await fetch(`/api/chat/session/${dialogId}/analysis/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        if (res.status === 202) {
          const queuedData = await res.json();
          if (queuedData.status === 'queued') {
            setQueuePosition(queuedData.position || 1);
            setShowAnalysisModal(true);
            data = await pollJob(queuedData.job_id, 'analysis');
          } else {
            data = queuedData;
          }
        } else {
          data = await res.json();
        }
      } catch (e) {
        setAnalysisError(true);
        setAnalysisErrorMessage('Ошибка сервера: получен невалидный ответ. Напишите позже.');
        setQueuePosition(null);
        return;
      }

      if (!res.ok && res.status !== 202 || data.error || !data.analysis) {
        const errMessage = data.error || 'Не удалось получить анализ. Напишите позже.';
        setAnalysisError(true);
        setAnalysisErrorMessage(errMessage);
        setError(errMessage);
        setQueuePosition(null);
        if (isRateLimitError(data, errMessage)) {
          const retryAfter = Number(data.retry_after_seconds) > 0 ? Number(data.retry_after_seconds) : RATE_LIMIT_COOLDOWN_SECONDS;
          activateAnalysisRetryCooldown(retryAfter);
          activateSendCooldown(retryAfter, 'Слишком много запросов. Напишите позже.');
        }
        return;
      }

      setAnalysis(data.analysis);
      setAnalysisError(false);
      setAnalysisErrorMessage('');
      setDialogStatus('completed');
      if (data.analysis_message?.text) {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'system' && last.text === data.analysis_message.text) {
            return prev;
          }
          return [...prev, { role: 'system', text: data.analysis_message.text }];
        });
      }
      fetchSessions();
    } catch (err) {
      const errMessage = err.message || 'Не удалось получить анализ. Напишите позже.';
      setAnalysisError(true);
      setAnalysisErrorMessage(errMessage);
      setError(errMessage);
      if (isRateLimitError(null, errMessage)) {
        activateAnalysisRetryCooldown(RATE_LIMIT_COOLDOWN_SECONDS);
        activateSendCooldown(RATE_LIMIT_COOLDOWN_SECONDS, 'Слишком много запросов. Напишите позже.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySend = () => {
    if (!lastFailedMessage || loading || dialogStatus !== 'active' || isSendTemporarilyBlocked) return;
    sendMessage(lastFailedMessage);
  };

  const isInputDisabled = loading || !dialogId || analysis || dialogStatus !== 'active' || isSendTemporarilyBlocked;

  return (
    <div className="h-screen w-full flex items-start justify-center bg-blue-50 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-950 py-2 sm:py-10 px-2 sm:px-6 transition-colors overflow-hidden">
      {/* Стили для typing-indicator и анализа */}
      <style>{`
        .typing-indicator-ui {
          display: inline-flex;
          align-items: center;
          background: #e3e7f1;
          color: #2d3748;
          border-radius: 18px;
          padding: 8px 18px;
          margin: 8px 0 8px 10px;
          font-size: 15px;
          font-style: italic;
          box-shadow: 0 2px 8px rgba(0,0,0,0.07);
          min-width: 100px;
          font-weight: 500;
        }
        .dark .typing-indicator-ui {
          background: #23272f;
          color: #e5e7eb;
        }
        .typing-indicator-ui .icon {
          margin-right: 8px;
          font-size: 18px;
        }
        .typing-indicator-ui .text {
          margin-right: 6px;
        }
        .typing-indicator-ui .dot {
          animation: blink 1.2s infinite both;
          font-weight: bold;
          font-size: 18px;
        }
        .typing-indicator-ui .dot:nth-child(3) { animation-delay: 0.2s; }
        .typing-indicator-ui .dot:nth-child(4) { animation-delay: 0.4s; }
        .typing-indicator-ui .dot:nth-child(5) { animation-delay: 0.6s; }
        @keyframes blink {
          0%, 80%, 100% { opacity: 0; }
          40% { opacity: 1; }
        }
        @media (min-width: 1024px) {
          .chat-main-flex { flex-direction: row; }
          .chat-panel { width: 70%; }
          .analysis-panel { width: 30%; min-width: 320px; max-width: 400px; }
        }
        @media (max-width: 1023px) {
          .chat-main-flex { flex-direction: column; }
          .chat-panel, .analysis-panel { width: 100%; }
        }
        .panel-btn { border-radius: 10px; font-weight: 700; font-size: 15px; padding: 14px 0; min-width: 120px; min-height: 44px; }
        input, textarea { font-size: 15px !important; min-height: 44px; }
        .dark input, .dark textarea {
          background: #23272f !important;
          color: #e5e7eb !important;
          border-color: #444 !important;
        }
        .dark .bg-white { background: #23272f !important; }
        .dark .bg-blue-50 { background: #181c23 !important; }
        .dark .text-blue-900 { color: #e5e7eb !important; }
        .dark .text-gray-600 { color: #b0b8c1 !important; }
        .dark .text-gray-400 { color: #8a8f98 !important; }
        .dark .bg-gray-200 { background: #23272f !important; color: #e5e7eb !important; }
        .dark .bg-blue-600 { background: #2563eb !important; }
        .dark .bg-green-100 { background: #1e293b !important; color: #a7f3d0 !important; border-color: #10b981 !important; }
        .dark .text-green-900 { color: #6ee7b7 !important; }
        .dark .border-green-400 { border-color: #10b981 !important; }
        .dark .bg-red-600 { background: #dc2626 !important; }
        .dark .hover\:bg-red-800:hover { background: #991b1b !important; }
        .dark .hover\:bg-blue-800:hover { background: #1e40af !important; }
        .dark .text-white { color: #e5e7eb !important; }
        .dark .border-gray-300 { border-color: #444 !important; }
        .dark .rounded-2xl { background: #23272f !important; }
        @media (max-width: 640px) {
          .panel-btn { font-size: 14px !important; padding: 12px 0 !important; min-width: 90px !important; min-height: 40px !important; }
          input, textarea { font-size: 13.5px !important; min-height: 38px; }
        }
      `}</style>
      <ToastContainer />
      {showAnalysisModal && (
        <AnalysisModal
          analysis={analysis}
          error={analysisError}
          errorMessage={analysisErrorMessage}
          onRetry={handleRetryAnalysis}
          retryInSeconds={analysisRetrySeconds}
          canRetry={!isAnalysisRetryBlocked && !loading && !!dialogId}
          queuePosition={queuePosition}
          onClose={() => {
            const shouldFinalizeDialog = !!analysis;
            setShowAnalysisModal(false);
            setAnalysisError(false);
            setAnalysisErrorMessage('');
            if (shouldFinalizeDialog) {
              setAnalysis(null);
              setAnalysisClosed(true);
            }
          }}
        />
      )}
      {newAchievements.length > 0 && (
        <AchievementsModal
          achievements={newAchievements}
          onClose={() => setNewAchievements([])}
        />
      )}
      <div className="w-full max-w-6xl flex gap-2">
        {/* Сайдбар истории (Desktop) */}
        {showSidebarDesktop ? (
          <div className={`${sidebarCollapsed ? 'hidden lg:flex lg:w-16' : 'hidden lg:block lg:w-1/3'} bg-white/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 ${sidebarCollapsed ? 'p-1.5' : 'p-3'} h-[80vh]`}
          >
            {/* Контент в развернутом состоянии */}
            {!sidebarCollapsed && (
              <div className="h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-base font-bold text-blue-900 dark:text-white">{showArchived ? 'Архив диалогов' : 'История диалогов'}</div>
                  <div className="flex items-center gap-2">
                    <button
                      className={`${showArchived ? 'px-2 py-1 rounded text-xs border bg-blue-600 text-white border-blue-600' : 'px-2 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-blue-900 dark:text-white'}`}
                      onClick={toggleArchivedView}
                      title={showArchived ? 'Показать историю' : 'Показать архив'}
                    >{showArchived ? 'История' : 'Архив'}</button>
                    {/* Крестик закрытия истории */}
                    <button
                      className="px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-red-600 hover:bg-red-50 dark:hover:bg-gray-900 text-xs font-bold"
                      title="Закрыть историю"
                      onClick={() => setShowSidebarDesktop(false)}
                    >✕</button>
                  </div>
                </div>
                {!showArchived && (
                  <div className="flex gap-2 mb-3">
                    <button
                      className={`${filterStatus === 'all' ? 'px-2 py-1 rounded text-xs border bg-blue-600 text-white border-blue-600' : 'px-2 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-blue-900 dark:text-white'}`}
                      onClick={() => handleChangeFilter('all')}
                    >Все</button>
                    <button
                      className={`${filterStatus === 'active' ? 'px-2 py-1 rounded text-xs border bg-blue-600 text-white border-blue-600' : 'px-2 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-blue-900 dark:text-white'}`}
                      onClick={() => handleChangeFilter('active')}
                    >Активные</button>
                    <button
                      className={`${filterStatus === 'completed' ? 'px-2 py-1 rounded text-xs border bg-blue-600 text-white border-blue-600' : 'px-2 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-blue-900 dark:text-white'}`}
                      onClick={() => handleChangeFilter('completed')}
                    >Завершённые</button>
                  </div>
                )}
                <div className="flex-1 overflow-y-auto">
                  {sessions.length === 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">{showArchived ? 'Архив пуст' : 'Нет сохранённых диалогов'}</div>
                  )}
                  <ul className="space-y-2">
                    {sessions.map(s => (
                      <li key={s.id}>
                        <div className="w-full p-2 rounded-md border border-gray-200 dark:border-gray-700">
                          <div className="flex items-start gap-2">
                            <button
                              className="flex-1 text-left min-w-0"
                              onClick={() => handleSelectSession(s)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-blue-900 dark:text-white truncate">{s.scenario_name || 'Сценарий'}</div>
                                <span className={`text-[10px] px-2 py-0.5 rounded ${s.status === 'completed' ? 'bg-green-100 text-green-900 dark:bg-gray-800 dark:text-green-200' : 'bg-blue-100 text-blue-900 dark:bg-gray-800 dark:text-white'}`}>{s.status}</span>
                              </div>
                              {s.last_message?.text && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1 overflow-hidden whitespace-nowrap text-ellipsis">{s.last_message.text}</div>
                              )}
                              <div className="text-[10px] text-gray-400 mt-1">
                                {s.completed_at ? 'Завершён' : 'Создан'}: {formatTime(s.completed_at || s.started_at)}
                              </div>
                            </button>
                            {/* Меню действий */}
                            <div className="relative">
                              <button
                                className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-900 rounded"
                                onClick={() => setMenuOpenId(menuOpenId === s.id ? null : s.id)}
                                title="Действия"
                              >⋯</button>
                              {menuOpenId === s.id && (
                                <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow z-10 min-w-[160px]">
                                  {showArchived ? (
                                    <button
                                      className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-900"
                                      onClick={() => { setMenuOpenId(null); handleRestore(s.id); }}
                                    >Разархивировать</button>
                                  ) : (
                                    <>
                                      <button
                                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-900"
                                        onClick={() => { setMenuOpenId(null); handleArchive(s.id); }}
                                      >Скрыть</button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Нижняя кнопка-стрелка для сворачивания */}
                <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700 flex justify-center">
                  <button
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 text-blue-900 dark:text-white shadow hover:bg-gray-100 dark:hover:bg-gray-900"
                    title="Свернуть историю"
                    onClick={() => setSidebarCollapsed(true)}
                  >
                    {"<"}
                  </button>
                </div>
              </div>
            )}

            {/* Компактное состояние: узкая рейка с кнопкой-разворотом */}
            {sidebarCollapsed && (
              <div className="h-full w-full flex flex-col items-center justify-end">
                <button
                  className="mb-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 text-blue-900 dark:text-white shadow hover:bg-gray-100 dark:hover:bg-gray-900"
                  title="Развернуть историю"
                  onClick={() => setSidebarCollapsed(false)}
                >
                  {">"}
                </button>
              </div>
            )}
          </div>
        ) : (
          // Кнопка-«ушко» для открытия истории (Desktop)
          <div className="hidden lg:flex w-6 items-start">
            <button
              className="mt-1 inline-flex items-center justify-center w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 text-blue-900 dark:text-white shadow hover:bg-gray-100 dark:hover:bg-gray-900"
              title="Показать историю"
              onClick={() => setShowSidebarDesktop(true)}
            >
              ☰
            </button>
          </div>
        )}

        {/* Панель чата */}
        <div className="flex-1 bg-white/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-1.5 sm:p-6 flex flex-col h-[80vh] min-h-0">
          {/* Кнопка открытия истории для мобильных */}
          <div className="lg:hidden mb-2 flex justify-between items-center">
            <button
              className="px-3 py-1 rounded text-xs border border-gray-300 dark:border-gray-600 text-blue-900 dark:text-white"
              onClick={() => setShowHistoryMobile(true)}
            >История</button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <button
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-red-300 bg-red-600 text-white text-xs sm:text-sm font-bold shadow hover:bg-red-700 transition disabled:opacity-50"
                title="Завершить диалог"
                onClick={handleFinish}
                disabled={loading || !dialogId || analysis || dialogStatus !== 'active' || isSendTemporarilyBlocked}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>✕</span>
                <span className="hidden sm:inline">Завершить диалог</span>
              </button>
            </div>
            <div className="mb-1 sm:mb-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl sm:text-3xl font-bold text-blue-900 dark:text-white break-words leading-tight">Диалог по сценарию</h1>
                <div className="flex items-center gap-2">
                  {dialogTokensUsed > 0 && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                      📊 -{dialogTokensUsed.toLocaleString()}
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${limits?.total_available === null ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                    limits?.total_available <= 0 ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                      limits?.total_available < 100 ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                        'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                    }`}>
                    🪙 {limits?.total_available !== null ? limits?.total_available?.toLocaleString() : '∞'}
                    {limits?.total_available !== null && limits?.total_available > 0 && limits?.total_available < 100 && ' ⚠️'}
                  </span>
                </div>
              </div>
              {limits?.total_available !== null && limits?.total_available > 0 && limits?.total_available < 100 && (
                <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">⚠️ Низкий баланс токенов</div>
              )}
              <div className="text-xs sm:text-base text-gray-600 dark:text-gray-300 mt-0.5 break-words">{selectedScenarioName || scenario?.name} — {scenario?.description || ''}</div>
              {dialogStatus !== 'active' && dialogId && (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Диалог завершён. Отправка сообщений недоступна.</div>
              )}
              {isSendTemporarilyBlocked && (
                <div className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                  Слишком много запросов. Напишите позже ({sendBlockSeconds} сек).
                </div>
              )}
            </div>
            {error && <div className="text-red-500 text-center mb-2 text-xs sm:text-base">{error}</div>}
            {dialogStatus === 'completed' && !analysis && analysisErrorMessage && (
              <div className="mb-2 flex items-center justify-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg border border-indigo-300 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs sm:text-sm font-semibold disabled:opacity-50"
                  onClick={handleRetryAnalysis}
                  disabled={loading || isAnalysisRetryBlocked}
                >
                  {isAnalysisRetryBlocked ? `Повторить анализ через ${analysisRetrySeconds} сек` : 'Повторить анализ'}
                </button>
              </div>
            )}
            {lastFailedMessage && dialogStatus === 'active' && (
              <div className="mb-2 flex items-center justify-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg border border-blue-300 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs sm:text-sm font-semibold disabled:opacity-50"
                  onClick={handleRetrySend}
                  disabled={loading || isSendTemporarilyBlocked}
                >
                  {isSendTemporarilyBlocked ? `Повторить отправку через ${sendBlockSeconds} сек` : 'Повторить отправку'}
                </button>
              </div>
            )}
            {/* Debug info для админа */}
            {debugInfo && !debugHidden && user?.role && ['admin', 'top_boss'].includes(user.role) && (
              <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg text-xs">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-yellow-800 dark:text-yellow-300">🔧 Debug: Полный запрос к LLM</div>
                  <button onClick={() => { setDebugHidden(true); localStorage.setItem('debugHidden', 'true'); }} className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200 text-lg px-2">✕</button>
                </div>
                <pre className="whitespace-pre-wrap text-yellow-700 dark:text-yellow-200 max-h-60 overflow-y-auto mt-1">{JSON.stringify(debugInfo.full_llm_request || debugInfo, null, 2)}</pre>
              </div>
            )}
            {debugHidden && debugInfo && user?.role && ['admin', 'top_boss'].includes(user.role) && (
              <button onClick={() => { setDebugHidden(false); localStorage.setItem('debugHidden', 'false'); }} className="mb-2 text-xs text-yellow-600 dark:text-yellow-400 hover:underline">🔧 Показать debug</button>
            )}
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto mb-1 sm:mb-4 bg-gradient-to-b from-blue-50/70 to-blue-100/40 dark:from-gray-900 dark:to-gray-950 rounded-xl p-2 sm:p-4 relative min-w-0 border border-gray-200 dark:border-gray-700"
              onScroll={e => {
                const container = e.target;
                const threshold = 120;
                const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
                setUserScrolledUp(!isAtBottom);
              }}
            >
              {messages.length === 0 && !loading && <div className="text-gray-400 text-center text-xs sm:text-base">Начните диалог с моделью…</div>}
              {loading && messages.length === 0 && <div className="text-gray-400 text-center text-xs sm:text-base">Загрузка сессии...</div>}
              {messages.map((msg, idx) => (
                <div key={idx} className={`mb-1 sm:mb-2 flex ${msg.role === 'user' ? 'justify-end' : msg.role === 'system' ? 'justify-center' : 'justify-start'}`}>
                  <div className={`px-3 sm:px-4 py-2 rounded-2xl max-w-[92%] sm:max-w-[80%] text-xs sm:text-base transition-all duration-200 shadow-md border 
                    ${msg.role === 'user' ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-600 dark:text-white dark:border-blue-600' :
                      msg.role === 'system' ? 'bg-green-50 text-green-900 border-green-300 dark:bg-gray-800 dark:text-green-200 dark:border-green-500' :
                        'bg-white text-gray-900 border-gray-200 dark:bg-gray-700 dark:text-white dark:border-gray-600'}`}
                    style={{ wordBreak: 'break-word', fontSize: '13px' }}>
                    {msg.role === 'assistant' ? (
                      <MarkdownRenderer variant="chat">
                        {msg.text}
                      </MarkdownRenderer>
                    ) : msg.text}
                  </div>
                </div>
              ))}
              {loading && !analysis && (
                <div className="mb-0.5 sm:mb-2 flex justify-start">
                  <TypingIndicator queuePosition={queuePosition} />
                </div>
              )}
              {/* Отчёт для завершённых диалогов */}
              {analysis && dialogStatus === 'completed' && (
                <div className="mb-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="font-semibold text-blue-800 dark:text-blue-200 mb-2">📊 Отчёт по диалогу</div>
                  <div className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-line">
                    <MarkdownRenderer variant="analysis">{analysis}</MarkdownRenderer>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
              {/* Удалено: синяя кнопка прокрутки страницы вниз */}
            </div>
            <div className="sticky bottom-0 left-0 right-0 pt-1 sm:pt-2 mt-auto bg-white/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md rounded-b-2xl">
              <div className="flex items-end gap-2">
                <textarea
                  className="flex-1 w-full border border-gray-300 dark:border-gray-600 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-sm
                            text-sm sm:text-base focus:outline-none focus:border-blue-600 
                            dark:focus:border-blue-400 bg-white dark:bg-gray-800 text-gray-900 
                            dark:text-white placeholder-gray-400 dark:placeholder-gray-400 
                            resize-none overflow-y-auto"
                  rows={1}
                  placeholder="Введите сообщение..."
                  value={input}
                  onChange={e => {
                    setInput(e.target.value);
                    e.target.style.height = 'auto';
                    const lineHeight = 24;
                    const maxRows = 6;
                    const maxHeight = lineHeight * maxRows;
                    e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + 'px';
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && e.shiftKey) return;
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!loading && dialogStatus === 'active' && !isSendTemporarilyBlocked && input.trim()) sendMessage();
                    }
                  }}
                  disabled={isInputDisabled}
                />
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 h-[44px] sm:h-[44px]
                            rounded-xl transition disabled:opacity-50 text-sm sm:text-base 
                            dark:bg-blue-600 dark:hover:bg-blue-800 dark:text-white"
                  onClick={() => sendMessage()}
                  disabled={isInputDisabled || !input.trim()}
                >
                  {loading ? "..." : "Отправить"}
                </button>
              </div>
            </div>
            {isTimed && (
              <div className="mt-2 sm:mt-4 text-center text-xs sm:text-lg text-blue-900 font-semibold">
                {endTime ? (
                  <>Вы завершили диалог за {Math.floor(duration / 60)}:{('0' + (duration % 60)).slice(-2)}</>
                ) : (
                  <>Время с начала диалога: {Math.floor(timer / 60)}:{('0' + (timer % 60)).slice(-2)}</>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Мобильная панель истории как оверлей */}
        {showHistoryMobile && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setShowHistoryMobile(false)}>
            <div className="absolute left-0 top-0 h-full w-10/12 max-w-xs bg-white dark:bg-gray-800 shadow-xl p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-base font-bold text-blue-900 dark:text-white">{showArchived ? 'Архив' : 'История'}</div>
                <button className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded" onClick={() => setShowHistoryMobile(false)}>Закрыть</button>
              </div>
              <ul className="space-y-2">
                {sessions.map(s => (
                  <li key={s.id}>
                    <button className="w-full text-left p-2 rounded-md border border-gray-200 dark:border-gray-700" onClick={() => { setShowHistoryMobile(false); handleSelectSession(s); }}>
                      <div className="text-sm font-semibold text-blue-900 dark:text-white truncate">{s.scenario_name || 'Сценарий'}</div>
                      {s.last_message?.text && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{s.last_message.text}</div>
                      )}
                      <div className="text-[10px] text-gray-400 mt-1">{s.completed_at ? 'Завершён' : 'Создан'}: {formatTime(s.completed_at || s.started_at)}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Модалка овердрафта */}
        {showOverdraftModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full shadow-xl">
              <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-3">Недостаточно токенов</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Ваш баланс токенов исчерпан. Пополните баланс, чтобы продолжить общение.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition"
                >
                  Пополнить
                </button>
                <button
                  onClick={() => setShowOverdraftModal(false)}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat; 
